import {getCurrentUserLogin} from '@github-ui/client-env'
import {createSafeIndexedDbPersister, SIMPLE_VALIDATOR} from '@github-ui/safe-indexed-db-storage'
import {sendEvent} from '@github-ui/hydro-analytics'

const ISSUE_SHOW_PATTERN = /^https?:\/\/[^/]+\/([^/]+)\/([^/]+)\/issues\/(\d+)(?:\?[^#]*)?(?:#.*)?$/

export interface PreheatIssuesConfig {
  /**
   * Returns whether preheating is enabled.
   */
  isEnabled: () => boolean
  /**
   * Identifies the caller so the preheat endpoint can track where
   * requests originate (e.g. `'pulls'`, `'discussions'`).
   */
  source: string
  /**
   * Whether the tabbed timeline experience is enabled for this user.
   * Affects both the cache key shape and the data the server returns.
   */
  tabbedTimeline: boolean
}

type PreheatEventBuffer = Array<{href?: string}>

/**
 * Framework-agnostic manager that preheats issue data by listening for
 * `issue-link-to-preheat` custom events and caching the responses via
 * `requestIdleCallback`.
 *
 * Usage:
 * ```ts
 * const manager = new PreheatIssuesManager({ isEnabled: () => true })
 * manager.start()
 * // …later…
 * manager.stop()
 * ```
 */
export class PreheatIssuesManager {
  #issues: Record<string, boolean> = {}
  #pending = false
  #cancelled = false
  #storage: ReturnType<typeof createSafeIndexedDbPersister>
  readonly #isEnabled: () => boolean
  readonly #source: string
  readonly #tabbedTimeline: boolean
  #boundHandleEvent: ((e: Event) => void) | null = null

  constructor(config: PreheatIssuesConfig) {
    this.#isEnabled = config.isEnabled
    this.#source = config.source
    this.#tabbedTimeline = config.tabbedTimeline
    this.#storage = createSafeIndexedDbPersister({
      validator: SIMPLE_VALIDATOR,
      sendAnalyticsEvent: (eventType, target?, payload = {}) => {
        sendEvent(eventType, {...payload, target})
      },
    })
  }

  /** Begin listening for preheat events and replay any buffered events. */
  start(): void {
    if (!this.#isEnabled()) return

    this.#cancelled = false

    // Replay any buffered events that fired before we registered.
    // __preheatEventBuffer is declared globally in issue-link.ts
    const buffer = (window as Window & {__preheatEventBuffer?: PreheatEventBuffer}).__preheatEventBuffer
    if (buffer && buffer.length > 0) {
      for (const detail of buffer) {
        if (detail.href) {
          this.#addIssue(detail.href)
        }
      }
      // Clear the buffer so events are not processed twice.
      buffer.length = 0
    }

    this.#boundHandleEvent = (e: Event) => {
      this.#addIssue((e as CustomEvent).detail.href)
    }

    window.addEventListener('issue-link-to-preheat', this.#boundHandleEvent)
  }

  /** Stop listening and cancel any in-flight idle callbacks. */
  stop(): void {
    this.#cancelled = true
    if (this.#boundHandleEvent) {
      window.removeEventListener('issue-link-to-preheat', this.#boundHandleEvent)
      this.#boundHandleEvent = null
    }
  }

  // ── Internal helpers ──────────────────────────────────────────────

  #addIssue(href: string): void {
    if (!ISSUE_SHOW_PATTERN.test(href)) return
    if (href in this.#issues) return
    this.#issues[href] = true
    this.#pending = true
    this.#scheduleNextPreheat()
  }

  #scheduleNextPreheat(): void {
    if (this.#cancelled || !('requestIdleCallback' in window)) return

    requestIdleCallback(() => {
      if (this.#cancelled || !this.#pending) return
      const runPreheat = async () => {
        await this.#preheatQueries()
        this.#scheduleNextPreheat()
      }
      void runPreheat()
    })
  }

  async #preheatQueries(): Promise<void> {
    const issuesToPreheat = Object.keys(this.#issues).filter(href => this.#issues[href])
    if (issuesToPreheat.length === 0) {
      this.#pending = false
      return
    }
    for (const href of issuesToPreheat) {
      this.#issues[href] = false
    }
    let noIssuesToPreheat = true
    for (const href of issuesToPreheat) {
      try {
        const urlMatch = href.match(ISSUE_SHOW_PATTERN)
        if (!urlMatch) return
        const [, owner, repo, number] = urlMatch
        if (!owner || !repo || !number) return

        const currentUserLogin = getCurrentUserLogin()
        const variables = JSON.stringify({
          count: 15,
          hideTimeline: false,
          number: Number(number),
          owner,
          repo,
          tabbedTimeline: this.#tabbedTimeline,
          ...(this.#tabbedTimeline ? {viewType: 'CONVERSATION'} : {}),
        })
        const cacheKey = `relay-query-${currentUserLogin}-IssueViewerViewQuery#${variables}`

        const cachedQuery = await this.#storage.getItem(cacheKey)
        if (cachedQuery) continue

        const response = await fetch(`/${owner}/${repo}/issues/preheat?number=${number}&source=${this.#source}`, {
          headers: {
            Accept: 'application/json',
            'X-GITHUB-PREHEAT': this.#source,
          },
        })
        if (response.status === 200) {
          const payload = await response.json()
          await this.#storage.setItem(cacheKey, payload.cacheValue)
        } else {
          continue
        }

        noIssuesToPreheat = false
        // eslint-disable-next-line unused-imports/no-unused-vars
      } catch (error) {
        // Ignore errors and continue preheating other queries
      }
    }
    if (noIssuesToPreheat) {
      this.#pending = false
    }
  }
}
